import dayjs from "dayjs";

import { matchPortalLanguage } from "../../../clientPortal/i18n";
import type { PortalAccessState } from "../../../clientPortal/types";
import { languageLabel } from "../personal/personalModel";

/** Which language the portal, intake and emails use for this client.
 * `setInPersonal`: follow with "Set a preferred language in Personal". */
export const portalLanguageNote = (
  preferred: string | null | undefined,
  firstName: string
): { text: string; setInPersonal: boolean } => {
  const portal = matchPortalLanguage(preferred);
  if (!preferred || portal === "en")
    return {
      text: "Portal, intake questions and emails are in English.",
      setInPersonal: true
    };
  if (portal)
    return {
      text: `Portal, intake questions and emails are in ${languageLabel(
        portal
      )}. ${firstName} can switch languages in the portal.`,
      setInPersonal: false
    };
  const label = languageLabel(preferred);
  return {
    text: `${firstName} prefers ${label}. The portal isn't available in ${label} yet, so it's in English.`,
    setInPersonal: false
  };
};

export type PortalAccessView = {
  label: string;
  tone: "off" | "pending" | "on" | "warn";
  description: string;
  /** The main button; null when nothing can be sent. */
  action: { label: string; kind: "invite" | "resend" } | null;
  canTurnOff: boolean;
};

const day = (iso: string | null | undefined) =>
  iso ? dayjs(iso).format("MMM D, YYYY") : "";

export const portalAccessView = (
  s: PortalAccessState,
  firstName: string,
  now: Date = new Date()
): PortalAccessView => {
  if (!s.email) {
    return {
      label: "Off",
      tone: "off",
      description: "No email on file. Add one in Personal to invite them.",
      action: null,
      canTurnOff: s.state !== "off"
    };
  }
  if (s.state === "off") {
    return {
      label: "Off",
      tone: "off",
      description: `Not invited. Inviting ${firstName} turns on their login and emails them a sign-in link. Intake links you send still work without it.`,
      action: { label: "Invite to Portal", kind: "invite" },
      canTurnOff: false
    };
  }
  if (s.state === "invited") {
    const expired = Boolean(
      s.invite_expires_at && dayjs(s.invite_expires_at).isBefore(now)
    );
    const by = s.invited_by ? ` by ${s.invited_by}` : "";
    return {
      label: expired ? "Invite Expired" : "Invited",
      tone: expired ? "warn" : "pending",
      description: expired
        ? `Invite sent ${day(s.invited_at)}${by}. The link expired ${day(
            s.invite_expires_at
          )} before ${firstName} used it. Resend to email a fresh one.`
        : `Invite sent ${day(
            s.invited_at
          )}${by}. ${firstName} hasn't signed in yet; the link expires ${day(
            s.invite_expires_at
          )}.`,
      action: { label: "Resend Invite", kind: "resend" },
      canTurnOff: true
    };
  }
  if (s.state === "active") {
    const seen = s.last_sign_in
      ? `Last signed in ${day(s.last_sign_in)}.`
      : `Joined ${day(s.accepted_at)}.`;
    return {
      label: "Active",
      tone: "on",
      description: `${seen} ${firstName} sees their tasks, case status and shared documents.`,
      action: { label: "Email a Sign-In Link", kind: "resend" },
      canTurnOff: true
    };
  }
  return {
    label: "On",
    tone: "on",
    description: `Login is on, but ${firstName} was never sent an invite. Send one so they know how to sign in.`,
    action: { label: "Send Invite", kind: "invite" },
    canTurnOff: true
  };
};
